import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./payment-processor.module.scss";
import { Button } from "../index";
import useWindowDimensions, { CurrentScreen } from "../../../../hooks/use-window-dimensions";
import { useLazyVerifyGuestTransactionQuery, useLazyVerifyTransactionQuery } from "../../../../service/charge-service";
import { VendingStatus } from "../../../../model/charge";
import { toast } from "react-toastify";
import { LoadingIconV2 } from "../../../../assets/icons";
import { resolveUtilityName } from "../../../../tenant/branding/resolveTenant";
import type { BillingFailureReason } from "../../../../services/business-analytics/vendEvents";

// Payment processor states
type PaymentState =
	| "redirecting" // About to redirect to payment gateway
	| "validating" // Validating payment after callback
	| "processing" // Processing in progress (status 1)
	| "success" // Payment successful (status 2)
	| "failed"; // Payment failed (status 3 or max retries)

export interface PaymentProcessorStatusMessages {
	redirecting?: { title: string; subtitle: string };
	validating?: { title: string; subtitle: string };
	processing?: { title: string; subtitle: string };
	success?: { title: string; subtitle: string };
	failed?: { title: string; subtitle: string };
}

interface PaymentProcessorProps {
	// For card payments - initial redirect
	paymentUrl?: string;
	reference?: string;
	vendingDetailsId?: string;
	// When set, verifies through the anonymous guest endpoint
	guestCustomerDetailsId?: string;

	// For wallet payments - direct validation
	skipRedirect?: boolean;

	// Callbacks
	onSuccess?: (reference: string) => void;
	onFailed?: (reference: string, reason: BillingFailureReason) => void;
	onBack?: () => void;
	onReferenceSet?: (reference: string) => void;

	// Display text customization
	redirectDelay?: number; // milliseconds
	statusMessages?: PaymentProcessorStatusMessages;

	// When true, the surface follows the light/dark customer theme (instead of the dark hero).
	themed?: boolean;
}

const defaultStatusMessages: Required<PaymentProcessorStatusMessages> = {
	redirecting: { title: "Redirecting to Payment", subtitle: "You will be redirected to the payment provider" },
	validating: { title: "Validating Payment", subtitle: "Please wait while we confirm your payment details" },
	processing: { title: "Processing Payment", subtitle: "Your payment is being processed" },
	success: { title: "Payment Successful", subtitle: "Your payment has been received" },
	failed: { title: "Payment Failed", subtitle: "Unable to complete your payment. Please try again." },
};

export const PaymentProcessor: React.FC<PaymentProcessorProps> = ({
	paymentUrl,
	reference: initialReference,
	vendingDetailsId,
	guestCustomerDetailsId,
	skipRedirect = false,
	onSuccess,
	onFailed,
	onBack,
	onReferenceSet,
	redirectDelay = 3000,
	statusMessages,
	themed = false,
}) => {
	const navigate = useNavigate();
	const currentScreen: CurrentScreen = useWindowDimensions();
	const [searchParams] = useSearchParams();

	const messages = { ...defaultStatusMessages, ...statusMessages };

	const [state, setState] = useState<PaymentState>(skipRedirect ? "validating" : "redirecting");
	const [countdown, setCountdown] = useState(Math.ceil(redirectDelay / 1000));
	const pollingAttemptsRef = useRef(0);
	const [reference, setReference] = useState<string | null>(initialReference || null);

	const [validatePayment] = useLazyVerifyTransactionQuery();
	const [validateGuestPayment] = useLazyVerifyGuestTransactionQuery();
	const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const hasRedirected = useRef(false);

	const MAX_POLLING_ATTEMPTS = 5;
	const POLLING_INTERVAL = 2000; // 2 seconds

	// Get reference from URL params (after callback)
	useEffect(() => {
		const urlReference = searchParams.get("reference");
		if (urlReference && !reference) {
			setReference(urlReference);
			if (onReferenceSet) {
				onReferenceSet(urlReference);
			}
			setState("validating");
		}
	}, [searchParams, reference]);

	// Countdown timer for redirect
	  useEffect(() => {
	    if (state === 'redirecting' && countdown > 0) {
	      const timer = setTimeout(() => {
	        setCountdown(countdown - 1);
	      }, 1000);
	      return () => clearTimeout(timer);
	    }
	  }, [state, countdown]);

	// Auto-redirect for card payments
	  useEffect(() => {
	    if (state === 'redirecting' && paymentUrl && !hasRedirected.current) {
	      const redirectTimer = setTimeout(() => {
	        handleRedirect();
	      }, redirectDelay);

	      return () => clearTimeout(redirectTimer);
	    }
	  }, [state, paymentUrl, redirectDelay]);

	// Start payment validation
	useEffect(() => {
		if ((state === "validating" || state === "processing") && reference) {
			validatePaymentStatus();
		}
	}, [state, reference]);

	const handleRedirect = () => {
		if (paymentUrl && !hasRedirected.current) {
			hasRedirected.current = true;
			window.location.href = paymentUrl;
		}
	};

	const validatePaymentStatus = async () => {
		if (!reference) return;

		// Check if max polling attempts reached
		if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
			stopPolling();
			setState("failed");
			toast.error("Payment verification timeout. Please check your transaction status or contact support.");
			if (onFailed) {
				onFailed(reference, "verification_timeout");
			}
			return;
		}

		// Increment polling attempts counter
		pollingAttemptsRef.current++;

		try {
			const response = guestCustomerDetailsId
				? await validateGuestPayment({ reference, guestCustomerDetailsId, utilityName: resolveUtilityName() }).unwrap()
				: await validatePayment({reference}).unwrap();

			const status = response.status as VendingStatus;

			switch (status) {
				case VendingStatus.COMPLETED:
					// Payment successful
					stopPolling();
					setState("success");
					setTimeout(() => {
						if (onSuccess) {
							onSuccess(response.reference);
						}
					}, 2000); // Show success for 2 seconds before callback
					break;

				case VendingStatus.FAILED:
					// Payment failed
					stopPolling();
					setState("failed");
					if (onFailed) {
						onFailed(reference, "payment_failed");
					}
					break;

				case VendingStatus.IN_PROGRESS:
					// Still processing - set state and start/continue polling
					if (state !== "processing") {
						setState("processing");
					}
					startPolling();
					break;

				case VendingStatus.UNKNOWN:
					// Unknown status - continue polling until max attempts
					startPolling();
					break;
			}
		} catch (error: any) {
			console.error("Payment validation error:", error);
			// On error, continue polling until max attempts reached
			startPolling();
		}
	};

	const startPolling = () => {
		// Clear any existing polling
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
		}

		// Start new polling interval
		pollingIntervalRef.current = setInterval(() => {
			validatePaymentStatus();
		}, POLLING_INTERVAL);
	};

	const stopPolling = () => {
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = null;
		}
		pollingAttemptsRef.current = 0;
	};

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			stopPolling();
		};
	}, []);

	const handleManualRedirect = () => {
		handleRedirect();
	};

	const handleGoBack = () => {
		stopPolling();
		if (onBack) {
			onBack();
		} else {
			navigate(-1);
		}
	};

	// Render different states
	const renderContent = () => {
		const currentMessages = messages[state] || defaultStatusMessages[state];
		return (
			<>
				<h2 className={styles.title}>{currentMessages.title}</h2>
				<p className={styles.subtitle}>{currentMessages.subtitle}</p>
			</>
		);
	};

	return (
		<div className={`${styles.paymentProcessorOverlay} ${themed ? styles.themed : ""}`}>
			<div className={styles.paymentProcessor}>
				<div className={styles.contentArea}>
					<div className={styles.iconContainer}>
							<div className={styles.loadingSpinner}>
								<LoadingIconV2 className={styles.spinner} />
							</div>
						</div>
						{renderContent()}
				</div>
			</div>
		</div>
	);
};

export default PaymentProcessor;
